import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Check, Zap, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { api, fetchPlans } from "../lib/api";
import { useAuth } from "../lib/auth";
import FlexibilityInitiative from "../components/FlexibilityInitiative";
import StripeCheckoutButton from "../components/StripeCheckoutButton";

const ACCENTS = {
  free: { bar: "bg-zinc-500", text: "text-zinc-300", border: "border-zinc-700" },
  starter: { bar: "bg-emerald-400", text: "text-emerald-300", border: "border-emerald-500/40" },
  pro: { bar: "bg-amber-400", text: "text-amber-300", border: "border-amber-500/60" },
  elite: { bar: "bg-purple-400", text: "text-purple-300", border: "border-purple-500/60" },
  team_monthly: { bar: "bg-pink-400", text: "text-pink-300", border: "border-pink-500/60" },
  team_unlimited: { bar: "bg-rose-400", text: "text-rose-300", border: "border-rose-500/60" },
  elite_annual: { bar: "bg-cyan-400", text: "text-cyan-300", border: "border-cyan-500/60" },
  pro_annual: { bar: "bg-indigo-400", text: "text-indigo-300", border: "border-indigo-500/60" },
};

const MONTHLY_IDS = new Set(["starter", "elite", "pro"]);
const TEAM_IDS = new Set(["team_monthly", "team_unlimited"]);
const ANNUAL_IDS = new Set(["elite_annual", "pro_annual"]);

function QtyPicker({ qty, setQty, testId, accent }) {
  const c = accent === "sky" ? "text-sky-300 border-sky-400/40" : "text-emerald-300 border-emerald-400/40";
  return (
    <div className="mt-3 flex items-center gap-2" data-testid={testId}>
      <span className="text-[10px] font-mono uppercase text-zinc-500">QTY</span>
      <button
        data-testid={`${testId}-minus`}
        onClick={() => setQty(Math.max(1, qty - 1))}
        className={`w-7 h-7 border ${c} font-mono hover:bg-white/5`}
      >
        −
      </button>
      <span className="w-8 text-center font-mono text-white tabnum">{qty}</span>
      <button
        data-testid={`${testId}-plus`}
        onClick={() => setQty(Math.min(10, qty + 1))}
        className={`w-7 h-7 border ${c} font-mono hover:bg-white/5`}
      >
        +
      </button>
      {qty >= 4 && (
        <span className="text-[10px] font-mono uppercase text-amber-300">
          +{Math.floor(qty / 4)} FREE · BUY-4-GET-5TH
        </span>
      )}
    </div>
  );
}

function PromoVoucherPanel({ promo, setPromo }) {
  const [promoInput, setPromoInput] = useState("");
  const [voucherInput, setVoucherInput] = useState("");
  const [busy, setBusy] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promos/validate", { code });
      if (data.valid && data.type === "promo") {
        setPromo(code);
        toast.success(`PROMO_VALID · ${code}`, {
          description: `${data.description} — applied when you subscribe below.`,
        });
      } else if (data.valid && data.type === "voucher") {
        toast.info("THAT'S A VOUCHER CODE", { description: "Use the voucher field to redeem it." });
      } else {
        toast.error("INVALID_PROMO", { description: data.reason || "code not recognized" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "validation failed");
    } finally {
      setBusy(false);
    }
  };

  const redeemVoucher = async () => {
    const code = voucherInput.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promos/vouchers/redeem", { code });
      toast.success(`VOUCHER_REDEEMED · +${data.uses_credited} USES`, {
        description: "Credited to your bonus balance instantly.",
      });
      setVoucherInput("");
    } catch (e) {
      toast.error("REDEEM_FAILED", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 border border-amber-400/30 bg-amber-500/[0.03] p-5" data-testid="pricing-promo-panel">
      <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
        PROMOS_&_VOUCHERS
      </span>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] font-mono text-zinc-400 mb-1.5">
            LAUNCH PROMO — 50% off your first month{promo && (
              <span className="text-emerald-300"> · {promo} ARMED ✓</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="promo-code-input"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value)}
              placeholder="PROMO CODE"
              className="flex-1 h-9 bg-[#0E0E0E] border border-[#27272A] px-3 text-[12px] font-mono text-white placeholder:text-zinc-600 focus:border-amber-400 outline-none uppercase"
            />
            <button
              data-testid="promo-apply-btn"
              onClick={applyPromo}
              disabled={busy}
              className="h-9 px-4 border border-amber-400 text-amber-300 font-mono text-[11px] uppercase tracking-wider hover:bg-amber-500/10 disabled:opacity-50"
            >
              APPLY
            </button>
          </div>
        </div>
        <div>
          <div className="text-[11px] font-mono text-zinc-400 mb-1.5">
            VOUCHER — each code = +10 bot uses ($9.99 value)
          </div>
          <div className="flex gap-2">
            <input
              data-testid="voucher-code-input"
              value={voucherInput}
              onChange={(e) => setVoucherInput(e.target.value)}
              placeholder="MSV-XXXXXXXX"
              className="flex-1 h-9 bg-[#0E0E0E] border border-[#27272A] px-3 text-[12px] font-mono text-white placeholder:text-zinc-600 focus:border-emerald-400 outline-none uppercase"
            />
            <button
              data-testid="voucher-redeem-btn"
              onClick={redeemVoucher}
              disabled={busy}
              className="h-9 px-4 border border-emerald-400 text-emerald-300 font-mono text-[11px] uppercase tracking-wider hover:bg-emerald-500/10 disabled:opacity-50"
            >
              REDEEM
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const { user, refreshAccount } = useAuth();
  const navigate = useNavigate();
  const [busyPlan, setBusyPlan] = useState(null);
  const [agreedFlex, setAgreedFlex] = useState(false);
  const [qty999, setQty999] = useState(1);
  const [qty9999, setQty9999] = useState(1);
  const [promo, setPromo] = useState("");

  const { data: plans, isLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: fetchPlans,
  });

  const startTrial = async () => {
    if (!agreedFlex) {
      toast.error("AGREE_TO_TERMS_FIRST", {
        description: "Please check [ I agree to the Terms & Conditions ] above the tiers.",
      });
      return;
    }
    if (!user) {
      navigate("/register?intent=trial");
      return;
    }
    try {
      setBusyPlan("trial");
      // Phase 26 — $0.01 verification (MOCKED) before starting the trial.
      try {
        await api.post("/billing/trial/verify");
      } catch (ve) {
        // Non-fatal in mock mode — log but continue so users aren't blocked.
        console.warn("trial verify (mock) failed:", ve?.message);
      }
      const { data } = await api.post("/billing/trial/start");
      await refreshAccount();
      toast.success(
        data.already_active
          ? "Trial already active — keep exploring."
          : "7-day free trial activated · 5 bonus MindStein usages granted."
      );
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally {
      setBusyPlan(null);
    }
  };

  const subscribe = async (planId) => {
    if (!agreedFlex) {
      toast.error("AGREE_TO_TERMS_FIRST", {
        description: "Please check [ I agree to the Terms & Conditions ] above the tiers.",
      });
      return;
    }
    if (!user) {
      navigate(`/register?plan=${planId}`);
      return;
    }
    try {
      setBusyPlan(planId);
      const promoQS = promo ? `&promo=${encodeURIComponent(promo)}` : "";
      const { data } = await api.post(`/billing/subscribe?plan_id=${planId}${promoQS}`);
      await refreshAccount();
      if (data?.promo_applied?.discount_pct) {
        toast.success(`PROMO_APPLIED · ${data.promo_applied.code}`, {
          description: `50% off your first month — $${(data.promo_applied.first_month_price_cents / 100).toFixed(2)}.`,
        });
      }
      const bonus = data?.first_subscription_bonus_granted || 0;
      if (bonus > 0) {
        toast.success(
          `Welcome to ${planId.toUpperCase()} · +${bonus} BONUS MINDSTEIN USAGES`,
          { description: "First-subscription gift — a free $9.99 top-up on the house." },
        );
      } else {
        toast.success(`Switched to ${planId.toUpperCase()} plan.`);
      }
      navigate("/account");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally {
      setBusyPlan(null);
    }
  };

  return (
    <div
      data-testid="pricing-page"
      className="min-h-screen bg-[#0A0A0A] text-white relative"
    >
      <div
        aria-hidden
        className="absolute inset-0 grid-bg opacity-30 pointer-events-none"
      />
      <header className="relative z-10 border-b border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-6">
          <Link
            to="/"
            data-testid="pricing-back-home"
            className="flex items-center gap-2 font-mono text-zinc-400 hover:text-white text-[12px]"
          >
            <ArrowLeft size={14} />
            <span className="inline-block w-2 h-2 bg-amber-400 animate-pulse" />
            <span className="text-base font-extrabold tracking-tight text-white">
              THE_MINDSTEIN
            </span>
          </Link>
          <nav className="ml-auto flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider">
            {user?.user_id ? (
              <Link
                to="/dashboard"
                data-testid="pricing-link-dashboard"
                className="px-3 h-8 flex items-center text-zinc-300 hover:text-white border border-[#27272A] hover:border-amber-400/60"
              >
                DASHBOARD
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="pricing-link-login"
                className="px-3 h-8 flex items-center text-zinc-300 hover:text-white border border-[#27272A] hover:border-amber-400/60"
              >
                SIGN_IN
              </Link>
            )}
          </nav>
        </div>
      </header>

      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/5 px-2 py-1 mb-5">
            <Sparkles size={12} className="text-amber-400" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
              7 TIERS · 7-DAY TRIAL · 50% ACTIVATION REBATE · HONOUR-THE-YEAR BONUS
            </span>
          </div>
          <h1 className="font-mono tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Pick a <span className="text-amber-400">tier</span>.
            <br />
            <span className="text-purple-400">Start free</span> for 7 days.
          </h1>
          <p className="mt-5 text-zinc-400 leading-relaxed max-w-xl mx-auto">
            Every paid feature unlocked during your trial · 5 free MindStein usages ·
            a <span className="text-emerald-300 font-mono">$0.01 verification</span>{" "}
            is placed on your card for honesty and{" "}
            <span className="text-emerald-300 font-mono">auto-refunded</span>{" "}
            the moment your account is verified. Your first paid subscription
            auto-grants a free{" "}
            <span className="text-emerald-300 font-mono">$9.99 top-up (+10 MindStein usages)</span>{" "}
            on the house.
          </p>

          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <button
              data-testid="pricing-cta-start-trial"
              onClick={startTrial}
              disabled={busyPlan === "trial"}
              className="h-11 px-5 inline-flex items-center gap-2 bg-amber-400 text-black border border-amber-400 hover:bg-amber-300 font-mono uppercase text-[12px] tracking-wider disabled:opacity-60"
            >
              <Zap size={14} />{" "}
              {busyPlan === "trial" ? "STARTING…" : "START_FREE_TRIAL"}
              <ArrowRight size={14} />
            </button>
            {!user && (
              <Link
                to="/login"
                data-testid="pricing-cta-sign-in"
                className="h-11 px-5 inline-flex items-center gap-2 border border-zinc-700 text-zinc-200 hover:bg-white hover:text-black font-mono uppercase text-[12px] tracking-wider"
              >
                SIGN_IN
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        {isLoading ? (
          <div className="text-center text-zinc-500 font-mono text-[12px]">
            LOADING_PLANS…
          </div>
        ) : (
          <>
            <FlexibilityInitiative
              agreed={agreedFlex}
              onAgreeChange={setAgreedFlex}
            />

            {/* ─── ANNUAL PLANS (SIDE-BY-SIDE) ─── */}
            {(() => {
              const annuals = (plans || []).filter((p) => ANNUAL_IDS.has(p.plan_id));
              if (!annuals.length) return null;
              return (
                <div
                  data-testid="pricing-annual-grid"
                  className="mb-10"
                >
                  <div className="mb-3 flex items-end justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-cyan-300">
                        ANNUAL PLANS · 12-MONTH PREPAY
                      </div>
                      <h2 className="font-mono text-2xl sm:text-3xl tracking-tight mt-1">
                        Best value — pay once, refill every 30 days.
                      </h2>
                      <p className="mt-1 text-[12px] text-zinc-400 max-w-xl">
                        Every annual plan auto-refills its MindStein quota every 30 days
                        for a full year and unlocks a{" "}
                        <span className="text-cyan-300 font-mono">HONOUR_THE_YEAR</span>{" "}
                        bonus after 12 successful renewals.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {annuals.map((p) => {
                      const accent = ACCENTS[p.plan_id] || ACCENTS.free;
                      const isCurrent = user?.account?.plan?.plan_id === p.plan_id;
                      const isTop = p.plan_id === "pro_annual";
                      const honour = p.honour_year_bonus_uses || 0;
                      return (
                        <div
                          key={p.plan_id}
                          data-testid={`pricing-plan-${p.plan_id}`}
                          className={`relative border ${accent.border} p-6 bg-gradient-to-br from-[#0E0E0E] to-[#0A0A0A] hover:from-[#141414]`}
                        >
                          {isTop && (
                            <div className="absolute -top-3 left-4 bg-indigo-400 text-black px-2 py-px text-[10px] font-mono uppercase tracking-[0.2em]">
                              TOP_TIER · BEST_VALUE
                            </div>
                          )}
                          <div className={`h-1 w-16 mb-3 ${accent.bar}`} />
                          <div className={`text-[10px] uppercase tracking-[0.2em] font-mono ${accent.text}`}>
                            {p.name.toUpperCase()}
                          </div>
                          <div className="mt-2 font-mono text-4xl tabnum">
                            ${(p.price_cents / 100).toFixed(2)}
                            <span className="text-zinc-500 text-[13px] ml-2">/ year</span>
                          </div>
                          <p className="mt-2 text-[13px] text-zinc-300 leading-relaxed">
                            {p.tagline}
                          </p>
                          <ul className="mt-4 space-y-1.5 text-[12.5px] text-zinc-300">
                            <li className="flex items-center gap-2">
                              <Check size={12} className={accent.text} />
                              <span>
                                <span className="text-white">{p.monthly_bot_quota} MindStein usages</span> every 30 days · auto-refill × 12
                              </span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className={accent.text} />
                              <span>Innovation Scout + Fun Facts + Idea Wall</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className={accent.text} />
                              <span>30-day prorated refund window</span>
                            </li>
                            {honour > 0 && (
                              <li className="flex items-center gap-2">
                                <Check size={12} className="text-emerald-300" />
                                <span>
                                  <span className="text-emerald-300 font-mono">+{honour} FREE MINDSTEIN USAGES</span> after
                                  honouring the year (12 renewals)
                                </span>
                              </li>
                            )}
                            <li className="flex items-center gap-2">
                              <Check size={12} className={accent.text} />
                              <span>+100 free uses on your first renewal (one-time)</span>
                            </li>
                          </ul>
                          <button
                            data-testid={`pricing-subscribe-${p.plan_id}`}
                            onClick={() => subscribe(p.plan_id)}
                            disabled={isCurrent || busyPlan === p.plan_id}
                            className={`mt-5 h-11 w-full inline-flex items-center justify-center gap-2 font-mono uppercase text-[12px] tracking-wider font-extrabold border ${
                              isCurrent
                                ? "border-zinc-700 text-zinc-500 cursor-not-allowed"
                                : isTop
                                ? "border-indigo-400 bg-indigo-400 text-black hover:bg-indigo-300"
                                : "border-cyan-400 bg-cyan-400 text-black hover:bg-cyan-300"
                            }`}
                          >
                            <Zap size={14} />
                            {isCurrent
                              ? "CURRENT_PLAN"
                              : busyPlan === p.plan_id
                              ? "SUBSCRIBING…"
                              : "GET_ANNUAL_PASS"}
                          </button>
                          <div className="mt-2 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 text-center">
                            ~ ${((p.price_cents / 100) / 12).toFixed(2)} / month equivalent
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ─── TEAMS SECTION (Phase 28) ─── */}
            {(() => {
              const teams = (plans || []).filter((p) => TEAM_IDS.has(p.plan_id));
              if (!teams.length) return null;
              return (
                <div
                  data-testid="pricing-teams-grid"
                  className="mb-10"
                >
                  <div className="mb-3 flex items-end justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-pink-300">
                        TEAMS · MONTHLY
                      </div>
                      <h2 className="font-mono text-2xl sm:text-3xl tracking-tight mt-1">
                        Bring your squad. One shared pool, unlimited creativity.
                      </h2>
                      <p className="mt-1 text-[12px] text-zinc-400 max-w-2xl">
                        Every Team plan ships with a <b className="text-pink-300">+100 welcome bonus</b>{" "}
                        and a <b className="text-pink-300">+50 uses per 5 verified seats</b> credit on the
                        2nd renewal.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {teams.map((p) => {
                      const accent = ACCENTS[p.plan_id] || ACCENTS.free;
                      const isCurrent = user?.account?.plan?.plan_id === p.plan_id;
                      const isUnlim = p.plan_id === "team_unlimited";
                      const extra = p.team_extra_seat_price_cents;
                      return (
                        <div
                          key={p.plan_id}
                          data-testid={`pricing-plan-${p.plan_id}`}
                          className={`relative border ${accent.border} p-6 bg-gradient-to-br from-[#0E0E0E] to-[#0A0A0A] hover:from-[#141414]`}
                        >
                          {isUnlim && (
                            <div className="absolute -top-3 left-4 bg-rose-400 text-black px-2 py-px text-[10px] font-mono uppercase tracking-[0.2em]">
                              UNLIMITED_SEATS
                            </div>
                          )}
                          <div className={`h-1 w-16 mb-3 ${accent.bar}`} />
                          <div className={`text-[10px] uppercase tracking-[0.2em] font-mono ${accent.text}`}>
                            {p.name.toUpperCase()}
                          </div>
                          <div className="mt-2 font-mono text-4xl tabnum">
                            ${(p.price_cents / 100).toFixed(2)}
                            <span className="text-zinc-500 text-[13px] ml-2">/ month</span>
                          </div>
                          <p className="mt-2 text-[13px] text-zinc-300 leading-relaxed">
                            {p.tagline}
                          </p>
                          <ul className="mt-4 space-y-1.5 text-[12.5px] text-zinc-300">
                            <li className="flex items-center gap-2">
                              <Check size={12} className={accent.text} />
                              <span>
                                <span className="text-white">
                                  {p.monthly_bot_quota} shared MindStein usages
                                </span>{" "}
                                every 30 days
                              </span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className={accent.text} />
                              <span>
                                {isUnlim
                                  ? "UNLIMITED seats · invite your whole org"
                                  : `${p.team_seats} seats included${extra ? ` · +$${(extra / 100).toFixed(0)}/extra seat` : ""}`}
                              </span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className="text-emerald-300" />
                              <span>
                                <span className="text-emerald-300 font-mono">+100 welcome bonus</span>{" "}
                                (one-time on commit)
                              </span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className="text-emerald-300" />
                              <span>
                                2nd-renewal bonus:{" "}
                                <span className="text-emerald-300 font-mono">
                                  +50 per 5 verified seats
                                </span>
                              </span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className="text-emerald-300" />
                              <span>
                                3rd-renewal bonus:{" "}
                                <span className="text-emerald-300 font-mono">
                                  +100 free MindStein usages
                                </span>{" "}
                                (one-time, both team plans)
                              </span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className={accent.text} />
                              <span>Personal + Shared library, PDF exports, Idea Wall</span>
                            </li>
                          </ul>
                          <button
                            data-testid={`pricing-subscribe-${p.plan_id}`}
                            onClick={() => subscribe(p.plan_id)}
                            disabled={isCurrent || busyPlan === p.plan_id}
                            className={`mt-5 h-11 w-full inline-flex items-center justify-center gap-2 font-mono uppercase text-[12px] tracking-wider font-extrabold border ${
                              isCurrent
                                ? "border-zinc-700 text-zinc-500 cursor-not-allowed"
                                : isUnlim
                                ? "border-rose-400 bg-rose-400 text-black hover:bg-rose-300"
                                : "border-pink-400 bg-pink-400 text-black hover:bg-pink-300"
                            }`}
                          >
                            <Zap size={14} />
                            {isCurrent
                              ? "CURRENT_PLAN"
                              : busyPlan === p.plan_id
                              ? "SUBSCRIBING…"
                              : `CHOOSE ${p.name.split(" ").pop().toUpperCase()}`}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ─── REGISTER & SUBSCRIPTIONS HEADLINE ─── */}
            <div
              data-testid="pricing-register-subscriptions-headline"
              className="flex items-end justify-between mb-4 flex-wrap gap-2"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
                  MONTHLY PLANS
                </div>
                <h2 className="font-mono text-2xl sm:text-3xl tracking-tight mt-1">
                  Register &amp; Subscriptions
                </h2>
                <p className="mt-1 text-[12px] text-zinc-400 max-w-xl">
                  Every monthly plan unlocks a{" "}
                  <span className="text-amber-300 font-mono">3RD_RENEWAL_DOUBLE_BONUS</span>{" "}
                  — accept the emailed offer and your MindStein usages double for that
                  cycle (refunds pause for that month per the accepted offer terms).
                </p>
              </div>
              <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">
                Updated {new Date().toISOString().slice(0, 10)} · v2.0
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {(plans || []).filter((p) => MONTHLY_IDS.has(p.plan_id)).map((p) => {
                const accent = ACCENTS[p.plan_id] || ACCENTS.free;
                const isCurrent = user?.account?.plan?.plan_id === p.plan_id;
                const isElite = p.plan_id === "elite";
                return (
                  <div
                    key={p.plan_id}
                    data-testid={`pricing-plan-${p.plan_id}`}
                    className={`relative flex flex-col border ${accent.border} bg-[#0E0E0E] p-5 hover:bg-[#141414]`}
                  >
                    {isElite && (
                      <div className="absolute -top-3 right-3 bg-purple-400 text-black px-2 py-px text-[9px] font-mono uppercase tracking-[0.2em]">
                        MOST POPULAR
                      </div>
                    )}
                    <div className={`h-1 w-12 mb-3 ${accent.bar}`} />
                    <div
                      className={`text-[10px] uppercase tracking-[0.2em] font-mono ${accent.text}`}
                    >
                      {p.name.toUpperCase()}
                    </div>
                    <div className="mt-2 font-mono text-3xl tabnum">
                      ${(p.price_cents / 100).toFixed(2)}
                      <span className="text-zinc-500 text-[12px] ml-1">/mo</span>
                    </div>
                    <p className="mt-2 text-[12px] text-zinc-400 leading-relaxed min-h-[44px]">
                      {p.tagline}
                    </p>
                    <ul className="mt-4 space-y-1.5 text-[12px] text-zinc-300 flex-1">
                      <li className="flex items-center gap-2">
                        <Check size={12} className={accent.text} />
                        <span>
                          {p.monthly_bot_quota > 0
                            ? `${p.monthly_bot_quota} MindStein usages every 30 days`
                            : "Browse + read libraries"}
                        </span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check size={12} className="text-amber-300" />
                        <span>
                          <span className="text-amber-300 font-mono">3rd-renewal</span> · double bonus month (email opt-in)
                        </span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check size={12} className={accent.text} />
                        <span>50% Activation Rebate (first 3 deployments)</span>
                      </li>
                    </ul>
                    <button
                      data-testid={`pricing-subscribe-${p.plan_id}`}
                      onClick={() => subscribe(p.plan_id)}
                      disabled={isCurrent || busyPlan === p.plan_id}
                      className={`mt-5 h-10 inline-flex items-center justify-center gap-2 font-mono uppercase text-[11px] tracking-wider border ${
                        isCurrent
                          ? "border-zinc-700 text-zinc-500 cursor-not-allowed"
                          : isElite
                          ? "border-purple-400 bg-purple-400 text-black hover:bg-purple-300"
                          : `border-zinc-700 text-zinc-200 hover:bg-white hover:text-black`
                      }`}
                    >
                      {isCurrent
                        ? "CURRENT_PLAN"
                        : busyPlan === p.plan_id
                        ? "SWITCHING…"
                        : user
                        ? "CHOOSE_PLAN"
                        : "GET_STARTED"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <PromoVoucherPanel promo={promo} setPromo={setPromo} />

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-emerald-500/30 bg-emerald-500/[0.04] p-5" data-testid="pricing-topup-9-99">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-emerald-300">
              MINDSTEIN TOP-UP · $9.99 PACK
            </span>
            <p className="mt-2 text-[13px] text-zinc-300 leading-relaxed">
              <span className="font-mono text-white">$9.99 = 10 MindStein usages</span>.
              <span className="text-emerald-300"> Buy 5, get +10 bonus uses</span> — unlimited purchases.
            </p>
            <QtyPicker qty={qty999} setQty={setQty999} testId="pricing-topup-qty" accent="emerald" />
            <StripeCheckoutButton
              lookupKey="topup_9_99"
              label={`BUY_${qty999}×_$9.99${qty999 >= 4 ? ` (+${Math.floor(qty999 / 4)} FREE)` : ""}`}
              testId="pricing-topup-buy"
              accent="emerald"
              quantity={qty999}
            />
          </div>
          <div className="border border-sky-500/30 bg-sky-500/[0.04] p-5" data-testid="pricing-topup-99-99">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-sky-300">
              BULK TOP-UP · $99.99 PACK
            </span>
            <p className="mt-2 text-[13px] text-zinc-300 leading-relaxed">
              <span className="font-mono text-white">$99.99 = 150 MindStein usages</span>.
              <span className="text-sky-300"> Buy 4 packs — the 5th is free</span>, auto-applied at checkout.
            </p>
            <QtyPicker qty={qty9999} setQty={setQty9999} testId="pricing-bulk-qty" accent="sky" />
            <StripeCheckoutButton
              lookupKey="topup_99_99"
              label={`BUY_${qty9999}×_$99.99${qty9999 >= 4 ? ` (+${Math.floor(qty9999 / 4)} FREE)` : ""}`}
              testId="pricing-bulk-buy"
              accent="emerald"
              quantity={qty9999}
            />
          </div>
          <div className="border border-fuchsia-500/40 bg-fuchsia-500/[0.05] p-5" data-testid="pricing-access-bonus">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-fuchsia-300">
              ACCESS BONUS UNLIMITED · $29.99 · PRE-LAUNCH
            </span>
            <p className="mt-2 text-[13px] text-zinc-300 leading-relaxed">
              <span className="font-mono text-white">$29.99 = 50 MindStein usages</span> · solo or team (up to 5 verified emails).
              Includes <span className="text-fuchsia-300">one free $9.99 ticket</span> and{" "}
              <span className="text-fuchsia-300">15% off first month</span> of any tier.
              Cancel for a full refund before your 8th use.
            </p>
            <StripeCheckoutButton
              lookupKey="access_bonus_29"
              label="BUY_ACCESS_BONUS_$29.99"
              testId="pricing-access-bonus-buy"
              accent="fuchsia"
            />
          </div>
        </div>

        <div className="mt-3 border border-purple-500/30 bg-purple-500/[0.04] p-5" data-testid="pricing-early-access">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-purple-300">
            EARLY ACCESS · $100 · 500-SEAT WINDOW
          </span>
          <p className="mt-2 text-[13px] text-zinc-300 leading-relaxed">
            <span className="font-mono text-white">$100 = 175 shared MindStein usages</span> across up to 5 verified emails.
            Now includes <span className="text-purple-300">three free $9.99 tickets</span> and{" "}
            <span className="text-purple-300">50% off first month</span> of any tier.
            One-time · non-refundable · seats deplete as packages sell.
          </p>
          <StripeCheckoutButton
            lookupKey="early_access_100"
            label="BUY_EARLY_ACCESS_$100"
            testId="pricing-early-access-buy"
            accent="purple"
          />
        </div>

        <div className="mt-3 border border-[#27272A] bg-[#0E0E0E] p-5 text-[12px] text-zinc-400 leading-relaxed">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500">
            FAIR_USE
          </span>
          <p className="mt-2">
            All tiers include unlimited reading of the topic libraries, Innovation
            Scout, and Fun Facts. Bot quotas reset every 30 days.{" "}
            <span className="text-amber-300 font-mono">Individual monthly plans</span> unlock a
            one-time <span className="text-amber-300 font-mono">3RD_RENEWAL_DOUBLE_BONUS</span>{" "}
            month (opt-in via email · refunds pause for that cycle). Annual plans
            receive <span className="text-emerald-300 font-mono">+50 (Standard)</span> or{" "}
            <span className="text-emerald-300 font-mono">+100 (Top-Tier)</span> free
            MindStein usages after honouring 12 renewals.{" "}
            <span className="text-pink-300 font-mono">Team plans</span> receive
            <span className="text-emerald-300 font-mono"> +100 welcome</span> on commit,
            <span className="text-emerald-300 font-mono"> +50 per 5 verified seats</span> on
            the 2nd renewal, and{" "}
            <span className="text-emerald-300 font-mono">+100 free MindStein usages</span> on the
            3rd renewal (activation rebate does not apply to team plans).
          </p>
        </div>
      </section>
    </div>
  );
}
